import { SAFContextVariable } from '@ems/yoda-ux';
import { EmeraldUserType } from '../../web/models/getHelp/emeraldUserType';
import { TopResultData } from '../../web/models/selfHelp';
import { AlchemyThemeRoutingNode, ThemeRoutingNodesMap } from '../packages/contactUs/models/alchemyThemeRoutingModels';

export interface BcqrResult {
    Content: string;
    Guid: string;
    Url: string;
    Title: string;
    Mode?: string;
}

export interface Modality {
    type: string;
    link: string;
    hoopsLink: string;
}

export enum AgeBracket {
    Child,
    Minor,
    Adult
}

export interface UserInfo {
    Cid: string;
    GivenName: string;
    IdentityProviderUrl: string;
    IsWindowsLiveDogfoodUser: boolean;
    Provider: string;
    Surname: string;
    MemberName: string;
    IsAdmin: boolean;
    IsBAUser: boolean;
    IsBATenant: boolean;
    LegalAgeGroup: number;
    DataBoundary: string[];
    IsDfM: boolean;
    UserPuid: string;
    TenantId: string;
    Entitlement: EntitlementResponseModel;
}

export interface EntitlementResponseModel {
    // If the tenant has unified premier contract, the tenant is an unified premier tenant.
    // We'll display critical/non-critical severity selection for the tenant.
    HasUnifiedContract: boolean;

    // If the tenant has unified enterprise premier contract, the tenant is an unified enterprise premier tenant.
    // We'll display Severity ABC dropdown for the tenant.
    HasUnifiedEnterpriseContract: boolean;

    // Entitlement Id of the tenant.
    // If a tenant has a premier Entitlement Id, the tenant is a premier tenant.
    // NonUnifiedPremier Tenant is when a tenant has a entitlement Id but doesn't have a unified contract or an unified enterprise contract.
    PremierEntitlementId: string;
}

export enum DayOfWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6
}

export interface BusinessHourData {
    StartHour: number;
    EndHour: number;
    StartMin: number;
    EndMin: number;
    StartDayOfWeek: DayOfWeek;
    EndDayOfWeek: DayOfWeek;
    TimeZoneName: string;
    UtcOffset: string;
}

export interface WaitTimeResponseInfo {
    BusinessHours: Array<BusinessHourData>;
}

export interface AnswerDeskContentModel {
    Message: string;
    Modalities: Map<AnswerDeskModalityType, AnswerDeskModality>;
}

export enum AnswerDeskModalityType {
    email = 'email',
    callback = 'callback',
    chat = 'chat',
    community = 'community',
    schedulecallback = 'schedulecallback',
    virtualagent = 'virtualagent',
    webmessenger = 'webmessenger',
    externallink = 'externallink',
    outofbusinesshours = 'OutOfBusinessHours'
}

export interface AnswerDeskModality {
    Link: string;
    ShowModality: boolean;
    IconClass: string;
    IconText: string;
    IconTextId: string;
    LinkId: string;
    LoggingBehavior: string;
    Info: WaitTimeResponseInfo;
}

export type AnswerDeskModalityWithType = AnswerDeskModality & { ModalityType: AnswerDeskModalityType };

export interface ClientTokenRetrievalInfoModel {
    Encryption: string;
    SiteDomain: string;
    SignInEndpoint: string;
    AADAppId: string;
    UserContext: string;
}

export interface AuthInfo {
    provider: string;
    token: string;
}

export enum AlchemySolutionsCallType {
    AlchemySolutions,
    AlchemyFetchNotifications,
    AlchemyUnAuthBranching,
    AlchemyProactive
}

export interface AlchemySolutionsCall {
    Url: string;
    Payload: AlchemySolutionsPayload;
    RequestHeaders: { 'Content-Type': string; 'X-Ms-DataBoundary': string[] };
}

export interface AlchemySolutionsPayload {
    App?: string;
    ClientContext?: IClientContext;
    RelatedSHCorrelationIds?: string;
    Text?: string;
    IsSpecialQuery?: boolean;
    BypassTextTranslation?: boolean;
    BypassSpellcheck?: boolean;
    UserType?: EmeraldUserType;
    Locale?: string;
}

// Outdated device entries to show with the outdated devices alert
export interface Device {
    Id: string;
    Name: string;
    Model?: string;
    OsVersion: string;
    Manufacturer: string;
}

// Alchemy models
export interface AlchemyResult {
    Theme: AlchemyThemeModel;
    Solutions: Array<AlchemySolutionModel>;
    Insights: Array<AlchemyInsightModel>;
    Incidents: Array<AlchemyIncidentModel>;
    AIInsights: AlchemyAIInsightsModel;
    CorrelationId: string;
    DebugInfo: string;
    ThemeRoutingData: AlchemyThemeRoutingData;
    OutdatedWindowsDevices?: Array<Device>;
    TopResults: TopResultData[];
    Query: string;
}

export interface AutoSuggestResult {
    title: string;
    '@search.text': string;
}

export interface AutoSuggestResultV2 {
    suggestions: Array<string>;
    searchText: Array<string>;
}

export interface AlchemyThemeModel {
    Name: string;
    Confidence: number;
    SecondaryTheme: string;
    SecondaryThemeConfidence: number;
}

export interface AlchemySolutionModel {
    Title: string;
    Description: string;
    Confidence: number;
    URI: string;
    Source: string;
    ContentType: ContentType;
}

// The resource of the ContentType in Solution Model newly added by Alchemy
export enum ContentType {
    // support.microsoft.com
    SMC = 'SMC',

    // answers.microsoft.com
    AMC = 'AMC',

    // docs.microsoft.com
    DMC = 'DMC',

    // learn.microsoft.com
    LMC = 'LMC'
}

// Determines which experiment we'll show for intergation AMC in BCQR
export enum BcqrExperimentType {
    // Only SMC
    Control = 0,

    // AMC and SMC but keep it's original order
    Experiment1 = 1,

    // AMC and SMC but promote AMC if no AMC in first three solutions
    Experiment2 = 2
}

export interface AlchemyInsightModel {
    Title: string;
    Description: string;
    TextFormat: string;
    BucketId: number;
    BucketName: string;
    BucketConfidence: number;
    BucketPercentile: number;
    RuleID: number;
    SolutionType: string;
    SolutionId: string;
    IsExpanded?: boolean; // this is client property for UI rendering only, and does not exist in the server model
}

export enum SolutionType {
    CustomText = 'CustomText',
    SOC = 'SOC',
    DMC = 'DMC',
    Multiple = 'Multiple',
    Branching = 'Branching',
    Portico = 'Portico'
}

export interface AlchemyIncidentModel {
    Id: string;
    WorkloadDisplayName: string;
    WorkloadName: string;
    FeatureDisplayName: string;
    Classification: string;
    StartTime: string;
    EndTime: string;
    LastUpdatedTime: string;
    ImpactDescription: string;
    Title: string;
    MessageText: string;
    MessagePublishedTime: string;
    Severity: string;
    AffectedTenantCount: number;
    IncidentStatusDisplayName: string;
    IncidentIsResolved: boolean;
    PublishedIncident: boolean;
}

export enum ContactFormBrand {
    GroupMe = 'groupme',
    SignUp = 'signup',
    Teams = 'teams',
    Default = 'default'
}

export interface LocalizedDaysOfWeek {
    LanguageName: string;
    DaysOfWeek: Map<string, string>;
}

export class AlchemyResourceRequestPayload {
    public SolutionType: string;
    public SolutionId: string;
    public Locale: string;
    public App?: string;
    public CorrelationId?: string;
    public SessionId?: string;
}

export class AlchemyResource {
    public SolutionTitle: string;
    public SolutionContent: string;
    public CorrelationId?: string;
}

export enum AlchemyApp {
    GetHelpRecommendation = 'GetHelpRecommendation',
    SupportCentral = 'SupportCentral',
    TeamsAdminApp = 'TeamsAdminApp'
}

export class AlchemyThemeRoutingData {
    // The Correlation Id for the theme routing flow
    public CorrelationId: string;

    // The locale that the data has been provided
    public Locale: string;

    // The Alchemy Theme, if provided
    public AlchemyTheme: string;

    // The list of child nodes, localized
    public Nodes: AlchemyThemeRoutingNode[];

    // Hash map of the nodes for faster lookup
    public NodesMap?: ThemeRoutingNodesMap;
}

export class SAFActionRequestPayload {
    public ActionId: string;
    public Locale: string;
    public App?: string;
    public CorrelationId?: string;
    public SessionId?: string;
}

export class SAFActionExecutionRequestPayload extends SAFActionRequestPayload {
    public Parameters: object;
    public ContextVariables: SAFContextVariable[];
    public V2: boolean;
}

export interface AlchemyAIInsightsModel {
    GPTInsight: string;
    FollowUpQuestions: string[];
    ScrubbedQuery: string;
}